import React from "react";
import { Icon } from "../core/Icon";
import { useViewport } from "../core/Viewport";
import { DanyAvatar } from "./DanyAvatar";

/**
 * The central ask-Dany input. Perplexity-shaped, FixyDany-skinned: paper fill,
 * 24px radius, deep warm shadow, Zippy sitting at the left as the "who am I
 * talking to" cue, and a gold send button that only lights up when there's text.
 *
 * PORT NOTE (DY1.4): three changes.
 *
 * `voiceDisabled` + `voiceTitle` — voice mode is DY1.6, but the mic is designed
 * into this control and removing it would change the composer's shape twice:
 * once now and once when voice lands. Disabled and labelled "coming soon" keeps
 * the geometry and tells the truth.
 *
 * **The ZIP pill leaves the box below 768px.** Inside a phone-width row it was
 * competing with the field for the same line — the avatar, the mic, the send
 * button and the pill left the textarea about 140px, which is not enough for
 * "Describe what needs fixing…" and is worse in Spanish, so the placeholder
 * wrapped and the control read as two stacked inputs. Above the box, right
 * aligned, it is a small piece of context about the search rather than part of
 * the sentence — which is what it always was. On desktop it stays inside,
 * where the row is wide enough for both.
 *
 * **The outline is ink, not gold.** See `--dany-composer-border` in
 * fixydany.css: gold is a fill in this system, and as a 1.5px rule at 1.3:1 it
 * is not an outline at all. The outer box is the only visible container — the
 * textarea has no border or background of its own, and inside the box the ZIP
 * loses its fill too, so nothing reads as a field nested in a field.
 *
 * **The camera works now** (T19 F4). It was drawn and wired to nothing while
 * Dany promised to look at photos. `onAttach(files)` receives the picked files
 * — one `<input accept="image/*">` with no `capture`, so a phone offers both
 * the camera and the gallery — and `attachBlocked` turns the tap into
 * `onAttachBlocked()` instead (a visitor: photos need an account, X5). The
 * button stays tappable then rather than `disabled`, because a disabled
 * control's only explanation is a `title`, and a touch screen has no hover.
 * `canSend` lets a turn with a photo and no words be sent.
 */
export function DanyComposer({
  value = "", onChange, onSubmit, placeholder = "Describe what needs fixing…",
  placeholderShort,
  zip, onZipChange, zipPlaceholder = "ZIP", size = "hero",
  attachments = true, voice = true, listening = false, onVoice, danyArt, footnote,
  voiceDisabled = false, voiceTitle, zipLabel,
  onAttach, attachTitle = "Attach a photo", attachBlocked = false,
  onAttachBlocked, attachBusy = false, canSend = false,
}) {
  const zipInput = React.useRef(null);
  const fileInput = React.useRef(null);
  const { isMobile } = useViewport();
  const hero = size === "hero";
  const ready = value.trim().length > 0 || canSend;
  const zipOutside = isMobile && !!onZipChange;

  /* Moving the ZIP pill out buys the field ~90px, and at 375px that still is
     not enough for the full prompt: the row also holds the avatar, the mic and
     the send button, which leaves the textarea about 176px against roughly
     208px of "Describe what needs fixing…" — and Spanish runs longer again.
     A `rows={1}` textarea does not ellipsise; it clips mid-word, which is what
     reads as broken. So a caller can hand over a shorter line for narrow
     screens. Falls back to the full one, so nothing changes for a caller that
     does not care. */
  const shown = isMobile && placeholderShort ? placeholderShort : placeholder;

  const zipField = (outside) => (
    // The whole pill is the target, not the ~40px of input inside it. Outside
    // the box it is a 44px control on a phone, and a thumb aimed at it should
    // not have to find the digits.
    <span
      onClick={() => zipInput.current?.focus()}
      className={outside ? "composer-shell" : undefined}
      style={{
      display: "inline-flex", alignItems: "center", gap: 5, alignSelf: "center",
      cursor: "text",
      padding: outside ? "7px 14px" : "6px 8px",
      minHeight: outside ? "var(--touch-min)" : undefined,
      borderRadius: "var(--radius-pill)",
      // Outside the box it is a control of its own and has to look tappable.
      // Inside, the box is the only container: a filled pill in there is a
      // field inside a field, which is the thing this change removes.
      background: outside ? "var(--surface-raised)" : "transparent",
      // Outside the box it is a control of its own and paints its own focus,
      // by the one pattern (`.composer-shell`, index.css): the rounded wrapper
      // raises the border it already has, and nothing draws a ring. Inside the
      // box it has no edge at all — the form around it is the wrapper, and
      // focus in here is focus within that.
      border: outside ? undefined : "1.5px solid transparent",
      color: "var(--text-muted)",
    }}>
      <Icon name="map-pin" size={13} />
      <input
        ref={zipInput}
        value={zip}
        onChange={(e) => onZipChange(e.target.value)}
        placeholder={zipPlaceholder}
        inputMode="numeric"
        maxLength={5}
        aria-label={zipLabel || zipPlaceholder}
        style={{
          width: outside ? "5.5ch" : 56, border: "none", outline: "none",
          background: "transparent", fontFamily: "var(--font-mono)",
          fontSize: "var(--text-sm)", color: "var(--text-body)",
        }}
      />
    </span>
  );

  return (
    <div style={{ width: "100%", maxWidth: hero ? 760 : "100%" }}>
      {zipOutside && (
        <div style={{
          display: "flex", justifyContent: "flex-end",
          marginBottom: "var(--space-2)",
        }} data-testid="dany-zip-outside">
          {zipField(true)}
        </div>
      )}
      {/* **The box is the only thing that paints focus** (T17-M1c). It used to
          do it from a React `focus` state and add `--dany-composer-shadow-focus`
          on top — a four-pixel ring around a box that already had a border,
          which on Jorge's phone read as a second edge. Now it is `:focus-within`
          in `.composer-shell` (index.css), shared with the chat composer, and
          all it does is take the same 1.5px stroke to full strength. The state
          went with it: the browser already tracks focus inside a subtree, and
          this box holds four focusable children. */}
      <form
        className="composer-shell"
        onSubmit={(e) => { e.preventDefault(); if (ready) onSubmit?.(); }}
        style={{
          display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: "var(--space-2)",
          padding: hero ? "clamp(10px, 2vw, 14px)" : "10px",
          background: "var(--dany-composer-bg)",
          borderRadius: "var(--dany-composer-radius)",
          boxShadow: "var(--dany-composer-shadow)",
        }}
      >
        {/* The "who am I talking to" cue costs 40px of row, and on a phone the
            hero already answers that question 100px above with a 92px ZIPPY and
            a headline. A third identity mark inside a 343px box is the ornament
            that pushes the prompt off the line. Desktop keeps it. */}
        {hero && !isMobile && (
          <span style={{ alignSelf: "center", marginRight: 2 }}>
            <DanyAvatar size={30} state={listening ? "listening" : "idle"} src={danyArt} />
          </span>
        )}
        <textarea
          rows={1}
          /* N9. The one selector anything outside this component uses to put
             the cursor here: "Ask Dany to change it" on the draft card has to
             reach the box that asks her, and a shared ref through three levels
             of context would be more machinery than one attribute. */
          data-dany-composer="true"
          value={value}
          placeholder={shown}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (ready) onSubmit?.(); } }}
          style={{
            // The field carries nothing of its own — no border, no fill, no
            // focus ring. The box around it is the container, and the focus
            // state belongs to the box, not to a rectangle drawn inside it.
            // `outline: none` was always here and was never the whole story:
            // `fixydany.css` puts a `box-shadow` on EVERY `:focus-visible` in
            // the app, so a copper rectangle was still being drawn inside the
            // 24px pill. `.composer-shell` cancels it (index.css, T17-M1c).
            flex: 1, border: "none", outline: "none", resize: "none",
            background: "transparent", fontFamily: "var(--font-body)",
            fontSize: hero ? "var(--text-lede-fluid)" : "var(--text-base)",
            lineHeight: "var(--leading-normal)", color: "var(--dany-text)",
            // 120, not 140: on a 375px screen the row has to hold the avatar,
            // the mic and the send button as well, and a floor that forces the
            // row to wrap is what put the field on its own line.
            minWidth: 120, minHeight: hero ? 34 : 26, padding: "8px 6px",
          }}
        />
        {onZipChange && !zipOutside && zipField(false)}
        {voice && (
          <button
            type="button"
            aria-label={voiceTitle || "Talk to Dany"}
            title={voiceTitle}
            onClick={voiceDisabled ? undefined : onVoice}
            disabled={voiceDisabled}
            aria-disabled={voiceDisabled || undefined}
            style={{
              width: "var(--touch-min)", height: "var(--touch-min)", alignSelf: "center",
              cursor: voiceDisabled ? "not-allowed" : "pointer",
              borderRadius: "var(--radius-pill)", border: "none",
              background: listening ? "var(--hive-a25)" : "transparent",
              color: listening ? "var(--hive-ink)" : "var(--text-muted)",
              opacity: voiceDisabled ? 0.45 : 1,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              transition: "background-color var(--duration-base) var(--ease-out)",
            }}>
            <Icon name={voiceDisabled ? "mic-off" : listening ? "audio-lines" : "mic"} size={18} />
          </button>
        )}
        {attachments && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length) onAttach?.(files);
                // So picking the same photo twice still fires `change`.
                e.target.value = "";
              }}
              data-testid="dany-attach-input"
            />
            <button
              type="button"
              aria-label={attachTitle}
              title={attachTitle}
              aria-disabled={attachBlocked || attachBusy || undefined}
              onClick={() => {
                if (attachBusy) return;
                if (attachBlocked) { onAttachBlocked?.(); return; }
                fileInput.current?.click();
              }}
              data-testid="dany-attach"
              style={{
                width: "var(--touch-min)", height: "var(--touch-min)", alignSelf: "center", border: "none",
                cursor: attachBusy ? "progress" : "pointer",
                borderRadius: "var(--radius-pill)", background: "transparent",
                color: "var(--text-muted)", display: "inline-flex", alignItems: "center", justifyContent: "center",
                opacity: attachBlocked ? 0.45 : 1,
              }}
            >
              <Icon name={attachBusy ? "loader-2" : "camera"} size={18} />
            </button>
          </>
        )}
        <button type="submit" aria-label="Ask Dany" disabled={!ready} style={{
          width: "var(--touch-min)", height: "var(--touch-min)", alignSelf: "center", marginLeft: "auto",
          border: "none", cursor: ready ? "pointer" : "not-allowed",
          borderRadius: "var(--radius-pill)",
          background: ready ? "var(--action-primary)" : "var(--sand-deep)",
          color: ready ? "var(--text-on-primary)" : "var(--paper)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          boxShadow: ready ? "var(--shadow-soft)" : "none",
          transition: "background-color var(--duration-base) var(--ease-out)",
        }}>
          <Icon name="arrow-up" size={19} />
        </button>
      </form>
      {footnote && (
        <p style={{ marginTop: "var(--space-3)", textAlign: "center", fontSize: "var(--text-xs)", color: "var(--dany-meta)" }}>
          {footnote}
        </p>
      )}
    </div>
  );
}
